import { useState, useEffect } from "react";
import { InlineWidget } from "react-calendly";
import { DIMS } from "../../data/Questions";
import { MATRIX, QUALITY } from "../../data/Scoring";
import "../../styles/sticky-pill.css";

/* ─────────────────────────────────────────────
   Shared result-section building blocks
───────────────────────────────────────────── */
function CalendlyBridge({ eyebrow, title, body, host = "both" }) {
  const showAlec = host === "both" || host === "alec";
  const showStan = host === "both" || host === "stan";

  return (
    <div className="calendly-bridge" id="calendly-bridge">
      <div className="cb-text">
        <div className="cb-eyebrow">{eyebrow}</div>
        <h3 className="cb-title" dangerouslySetInnerHTML={{ __html: title }} />
        <p className="cb-body">{body}</p>
        <p className="cb-body">
          No pressure. No pitch. Just a genuine conversation.
        </p>
        <div className="cb-meta">
          <div className="cb-meta-item">
            <span>⏱</span>
            <span>15 minutes</span>
          </div>
          <div className="cb-meta-item">
            <span>🎥</span>
            <span>Video call</span>
          </div>
          <div className="cb-meta-item">
            <span>✦</span>
            <span>Free</span>
          </div>
        </div>
      </div>
      <div
        className={host === "both" ? "cb-hosts" : "cb-hosts cb-hosts--single"}
      >
        {showAlec && (
          <div className="cb-host-card">
            <div className="cp-avatars">
              <div className="cp-avatar">A</div>
            </div>
            <p className="cp-name">Alec Lance</p>
            <p className="cp-role">Co-Founder, Synergistic Intelligence</p>
            <div className="cp-divider"></div>
            <p className="cp-instruction">Select a time that works for you</p>
            <div className="cb-widget-wrap">
              <InlineWidget
                url="https://calendly.com/alec-synergisticintelligence/post-fia-connect-with-alec"
                styles={{ height: "700px", width: "100%" }}
                pageSettings={{
                  backgroundColor: "f8f4ee",
                  primaryColor: "5c6b4a",
                  textColor: "2e2820",
                  hideEventTypeDetails: true,
                  hideLandingPageDetails: true,
                }}
              />
            </div>
          </div>
        )}

        {showStan && (
          <div className="cb-host-card">
            <div className="cp-avatars">
              <div className="cp-avatar">S</div>
            </div>
            <p className="cp-name">Stan Cross</p>
            <p className="cp-role">Co-Founder, Synergistic Intelligence</p>
            <div className="cp-divider"></div>
            <p className="cp-instruction">Select a time that works for you</p>
            <div className="cb-widget-wrap">
              <InlineWidget
                url="https://calendly.com/stan-synergisticintelligence/30min"
                styles={{ height: "700px", width: "100%" }}
                pageSettings={{
                  backgroundColor: "f8f2e8",
                  primaryColor: "6f8f5a",
                  textColor: "2f2923",
                  hideEventTypeDetails: true,
                  hideLandingPageDetails: true,
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Resources({ extra = null, onEmailResults, emailStatus = "idle" }) {
  const buttonLabel = {
    idle: "Email me my results & resources",
    sending: "Sending…",
    sent: "Sent — check your inbox",
    error: "Something went wrong — try again",
  }[emailStatus];

  return (
    <div className="results-next">
      <h3>A few things to take with you.</h3>
      <p>Whatever you decide, these are yours.</p>
      <div className="results-next-items">
        <div className="rni">
          <span className="rni-icon">📄</span>
          <div className="rni-text">
            <strong>Flow Journey Guide — Preview</strong>
            <span>The Intro and Survival Instincts sections.</span>
          </div>
        </div>
        <a
          href="/flow-journey"
          target="_blank"
          rel="noopener noreferrer"
          className="rni rni--link"
        >
          <span className="rni-icon">🌊</span>
          <div className="rni-text">
            <strong>Explore the Flow Journey</strong>
            <span>
              Learn how the journey works, what to expect, and whether it might
              be right for you.
            </span>
          </div>
          <span className="rni-arrow">→</span>
        </a>
        <div className="rni">
          <span className="rni-icon">✉️</span>
          <div className="rni-text">
            <strong>Stan's Substack</strong>
            <span>
              Writing on the territory you already know from the inside.
            </span>
          </div>
        </div>
        {extra}
      </div>
      <div className="results-cta-group">
        <button
          type="button"
          className="btn-results-primary"
          onClick={onEmailResults}
          disabled={emailStatus === "sending" || emailStatus === "sent"}
        >
          {buttonLabel}
        </button>
        <a
            href="https://substack.com/@synergisticvision"
            className="btn-results-ghost"
            target="_blank"
            rel="noopener noreferrer"
          >
            Read Stan's Substack
          </a>
      </div>
    </div>
  );
}

function QualityPull({ stuck }) {
  const q = QUALITY[stuck];
  if (!q) return null;
  return (
    <div className="results-pull">
      <p>
        One quality seems to be asking for a little more attention right now:{" "}
        <strong style={{ fontStyle: "normal" }}>{q.name}</strong> — {q.desc}.
      </p>
    </div>
  );
}

function CarriedQuestion({ place, stuck, follow }) {
  const row = MATRIX[place];
  const cell = row[stuck] || row.default;
  return (
    <div className="carried-q">
      <div className="cq-label">The question you seem to be carrying</div>
      <p className="cq-question">"{cell.q}"</p>
      <p className="cq-follow">{follow}</p>
    </div>
  );
}

/* ─────────────────────────────────────────────
   RESULTS · four states
   onEmailResults: callback for "Email me my results" CTA (wire up to your backend)
───────────────────────────────────────────── */
function ResultA({ onEmailResults, emailStatus }) {
  return (
    <>
      <div className="results-hero">
        <div className="results-eyebrow">Where you are right now</div>
        <div className="place-name">
          In a <em>working season</em>.
        </div>
        <div className="results-tagline">
          Your approach to life is doing its job. Clarity, logic, momentum — the
          system works. That's not nothing. It's worth respecting.
        </div>
      </div>
      <div className="results-section">
        <h3>What your responses suggest</h3>
        <div className="results-body">
          <p>
            You trust what you can reason through, and that trust has been
            earned — it's carried you a long way. Right now, there's no strong
            signal in your life saying otherwise, and we're not going to pretend
            there is. <strong>Nothing here needs fixing.</strong>
          </p>
          <p>
            The only thing worth saying is this: seasons change. For most of the
            people who do this work, it didn't begin with a crisis. It began
            with something small and easy to miss. If a different season ever
            arrives, it helps to recognize what it sounds like.
          </p>
        </div>
      </div>
      <div className="results-section">
        <h3>What it might sound like, if it comes</h3>
        <div className="signal-list">
          <div className="signal-item">
            <div>
              <strong>A quiet question</strong>One the usual answers don't quite
              satisfy — underneath the momentum, easy to override.
            </div>
          </div>
          <div className="signal-item">
            <div>
              <strong>A thirst that achievement doesn't touch</strong>The next
              milestone keeps promising to deliver something. It keeps not quite
              arriving.
            </div>
          </div>
          <div className="signal-item">
            <div>
              <strong>A shortage of real conversation</strong>Plenty of capable
              people around — and less and less dialogue where nobody's
              performing.
            </div>
          </div>
          <div className="signal-item">
            <div>
              <strong>A sense about the strategy itself</strong>A growing
              feeling that waiting for the next achievement to finally feel like
              enough might not be the plan.
            </div>
          </div>
        </div>
      </div>
      <div className="story-offer">
        <div className="so-label">One thing, if you're curious</div>
        <p className="so-title">Not an argument. Just a story.</p>
        <p className="so-body">
          Alec recently had a bristlecone moment in the Eastern Sierra — a quiet
          encounter that made him wonder what else might be right in front of
          us, just outside our awareness. It's a short reflection on noticing,
          awareness, and what can emerge when a moment unexpectedly seizes us.
          <br />
          <a
            href="https://lnkd.in/p/ghYtp5qt"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "var(--moss)",
              fontWeight: "600",
              display: "inline-block",
              marginTop: "0.75rem",
            }}
          >
            <em>Read "Just Outside My Awareness—A Bristlecone Moment" →</em>
          </a>
        </p>
      </div>
      <div className="results-next">
        <h3>That's it. No pitch.</h3>
        <p>
          If a quieter question ever starts to surface, we'll be here. In the
          meantime, occasional writing on this territory lands here:
        </p>
        <div className="results-next-items">
          <div className="rni">
            <span className="rni-icon">✉️</span>
            <div className="rni-text">
              <strong>Alec's Substack</strong>
              <span>
                Short reflections on awareness, attention, and the things that
                sit just outside our awareness.
              </span>
            </div>
          </div>
        </div>
        <div className="results-cta-group">
          <button
            type="button"
            className="btn-results-primary"
            onClick={onEmailResults}
            disabled={emailStatus === "sending" || emailStatus === "sent"}
          >
            {
              {
                idle: "Email me my results",
                sending: "Sending…",
                sent: "Sent — check your inbox",
                error: "Something went wrong — try again",
              }[emailStatus || "idle"]
            }
          </button>
          <a
            href="https://substack.com/@alecl1"
            className="btn-results-ghost"
            target="_blank"
            rel="noopener noreferrer"
          >
            Read Alec's Substack
          </a>
        </div>
      </div>
    </>
  );
}

function ResultB({ result, onEmailResults, emailStatus }) {
  return (
    <>
      <div className="results-hero">
        <div className="results-eyebrow">Where you are right now</div>
        <div className="place-name">
          At the <em>threshold</em>.
        </div>
        <div className="results-tagline">
          Something quiet has started asking for your attention. You haven't yet
          decided whether to listen. That's not a flaw — it's the earliest and
          most valuable signal there is.
        </div>
      </div>
      <div className="results-section">
        <h3>What your responses suggest</h3>
        <div className="results-body">
          <p>
            There's a signal in your life right now — a flatness where
            excitement used to be, a quiet unease in moments that look fine from
            the outside. You notice it. And mostly, you set it aside. Maybe you
            even feel a little guilty for feeling it at all, given how much is
            genuinely good.
          </p>
          <p>
            Both of those are real: the life that works, and the feeling that
            something in it is asking a question.{" "}
            <strong>
              You don't have to choose between them yet. You only have to stop
              pretending the question isn't there.
            </strong>
          </p>
          <p>
            Here's what matters most: you're noticing this early — while
            everything is still stable. That stability isn't a reason to dismiss
            the signal. It's what makes it possible to explore it from strength,
            moving toward something rather than away from something.
          </p>
        </div>
      </div>
      <QualityPull stuck={result.stuck} />
      <CarriedQuestion
        place="B"
        stuck={result.stuck}
        follow="You don't need an answer today. The feeling isn't a problem to solve — it's a message that hasn't been allowed to finish its sentence. Letting it speak is the whole first step."
      />
      <CalendlyBridge
        eyebrow="If you want company with it"
        title="Sometimes the signal gets clearer<br>when it's spoken aloud."
        body="Fifteen minutes with Alec or Stan — not to be sold anything, and not to commit to anything. Just to say out loud what you've been noticing, to someone who once set the same feeling aside for years."
      />
      <Resources onEmailResults={onEmailResults} emailStatus={emailStatus} />
    </>
  );
}

function ResultC({ result, onEmailResults, emailStatus }) {
  return (
    <>
      <div className="results-hero">
        <div className="results-eyebrow">Where you are right now</div>
        <div className="place-name">
          In the <em>questioning</em>.
        </div>
        <div className="results-tagline">
          You've started to see the pattern. You haven't yet decided what to do
          with it. That's not a problem to fix — it's a threshold, and you're
          standing on it.
        </div>
      </div>
      <div className="results-section">
        <h3>What your responses suggest</h3>
        <div className="results-body">
          <p>
            You're no longer able to fully ignore the signal. When something
            feels off — in a decision, a role, a direction — you notice it now.
            You've even begun to wonder what it's been costing you to override
            it.
          </p>
          <p>
            And still, something holds. There's a quiet, understandable loyalty
            to the way things are — the structure you built, the version of
            yourself that works. Both things are true at once: you can see it,
            and you're still inside it.{" "}
            <strong>That's exactly what this stage feels like.</strong>
          </p>
          <p>
            It's also the stage where real change becomes possible — because you
            still have the stability and the clarity to move <em>toward</em>{" "}
            something, rather than away from something in collapse.
          </p>
        </div>
      </div>
      <QualityPull stuck={result.stuck} />
      <CarriedQuestion
        place="C"
        stuck={result.stuck}
        follow="You don't need to answer it today. It's the kind of question that opens slowly — and it's a good one to sit with. It's also a good one to bring to a conversation."
      />
      <CalendlyBridge
        eyebrow="A natural next step"
        title="Bring the question<br>to a conversation."
        body="Fifteen minutes with Alec or Stan — not to be sold anything, but to look at what you noticed here, together. Sometimes a question opens faster when it's spoken aloud to someone who's been there."
      />
      <Resources onEmailResults={onEmailResults} emailStatus={emailStatus} />
    </>
  );
}

function ResultD({ result, onEmailResults, emailStatus }) {
  const frontier = DIMS[result.stuck] || "";
  return (
    <>
      <div className="results-hero">
        <div className="results-eyebrow">Where you are right now</div>
        <div className="place-name">
          Living <em>from flow</em>.
        </div>
        <div className="results-tagline">
          Much of what this assessment looks for, you've already crossed. Not as
          a finish line — as a way of meeting the world that you've earned, and
          keep earning.
        </div>
      </div>
      <div className="results-section">
        <h3>What your responses suggest</h3>
        <div className="results-body">
          <p>
            You've learned to treat internal signals as information rather than
            noise. When aliveness leaves a direction, you notice. When effort
            becomes the problem, you can release. That didn't come free — your
            answers suggest it cost you something to learn, which is usually how
            the real learning goes.
          </p>
          <p>
            One honest note: your responses hint that{" "}
            <strong>{frontier.toLowerCase()}</strong> may still be the tender
            frontier — the place where the crossing is most recent, or least
            finished. You probably already know that. Frontiers like that don't
            close; they just become familiar territory.
          </p>
          <p>
            Which raises the more interesting question — the one that tends to
            find people where you are.
          </p>
        </div>
      </div>
      <CarriedQuestion
        place="D"
        stuck={result.stuck}
        follow="At a certain point the work turns outward. What you've learned in crossing is exactly what someone near you can't yet see. That's not an obligation — it's an invitation."
      />
      <div className="story-offer">
        <div className="so-label">An invitation, not a funnel</div>
        <p className="so-title">
          If you've crossed something real to get here — we'd love to hear the
          story.
        </p>
        <p className="so-body">
          We host conversations with people who've made this crossing, about
          what it actually took. If telling that story appeals to you, forward
          your results email and say so. A real human — Alec or Stan — will read
          it and write back.
        </p>
      </div>
      <CalendlyBridge
        host="stan"
        eyebrow="A conversation, if it's useful"
        title="Talk it through with Stan"
        body="If the tender frontier you're standing at could use another set of eyes, Stan's happy to spend fifteen minutes with you on it — no agenda beyond that."
      />
      <div className="results-next">
        <h3>A few things to take with you.</h3>
        <p>These are yours either way.</p>
        <div className="results-next-items">
          {/* <div className="rni">
            <span className="rni-icon">🎧</span>
            <div className="rni-text">
              <strong>Quantum Emergence — Audio Introduction</strong>
              <span>Stan Cross reads the opening of the allegory.</span>
            </div>
          </div> */}
          <div className="rni">
            <span className="rni-icon">✉️</span>
            <div className="rni-text">
              <strong>Stan's Substack</strong>
              <span>
                Writing on the territory you already know from the inside.
              </span>
            </div>
          </div>
        </div>
        <div className="results-cta-group">
          <button
            type="button"
            className="btn-results-primary"
            onClick={onEmailResults}
            disabled={emailStatus === "sending" || emailStatus === "sent"}
          >
            {
              {
                idle: "Email me my results & resources",
                sending: "Sending…",
                sent: "Sent — check your inbox",
                error: "Something went wrong — try again",
              }[emailStatus || "idle"]
            }
          </button>
          <a
            href="https://substack.com/@synergisticvision"
            className="btn-results-ghost"
            target="_blank"
            rel="noopener noreferrer"
          >
            Read Stan's Substack
          </a>
        </div>
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────
   Sticky pill · shown only for B and C results.
   Fades in slowly after the results settle, then
   stays pinned bottom-center and links to the
   Flow Journey page (opens in a new tab so the
   person's results stay on screen).
───────────────────────────────────────────── */
function StickyPill() {
  const [visible, setVisible] = useState(false);

  // Delay the entrance so the person can take in the result first
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 2000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="sticky-pill-wrap" aria-hidden={!visible}>
      <a
        href="/flow-journey"
        target="_blank"
        rel="noopener noreferrer"
        className={`sticky-pill${visible ? " is-visible" : ""}`}
        tabIndex={visible ? 0 : -1}
      >
        <span className="sp-message">
          Your Flow Identity Assessment answers suggest that you're a good fit
          for the Flow Journey.
        </span>
        <span className="sp-cta">
          We invite you to explore this offering
          <span className="sp-arrow">→</span>
        </span>
      </a>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Results · top-level screen component
───────────────────────────────────────────── */
export default function Results({ result, onEmailResults, emailStatus }) {
  let resultContent;

  switch (result.place) {
    case "A":
      resultContent = (
        <ResultA
          result={result}
          onEmailResults={onEmailResults}
          emailStatus={emailStatus}
        />
      );
      break;

    case "B":
      resultContent = (
        <ResultB
          result={result}
          onEmailResults={onEmailResults}
          emailStatus={emailStatus}
        />
      );
      break;

    case "C":
      resultContent = (
        <ResultC
          result={result}
          onEmailResults={onEmailResults}
          emailStatus={emailStatus}
        />
      );
      break;

    case "D":
      resultContent = (
        <ResultD
          result={result}
          onEmailResults={onEmailResults}
          emailStatus={emailStatus}
        />
      );
      break;

    default:
      resultContent = null;
  }

  const showPill = result.place === "B" || result.place === "C";

  return (
    <div className="results">
      {resultContent}
      {showPill && <StickyPill />}
    </div>
  );
}